import { useEffect, useState } from "react";
import { Card, Dropdown, ListGroup, Nav } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import ApiTable from "../../../@ui/ApiTable/ApiTable";
import useQueueService from "../../../hooks/useQueueService";
import { ApiTableState } from "../../../types/ApiTable/ApiTableState";
import { ApiTableColumn } from "../../../types/ApiTable/ApiTableColumn";
import { Pagination } from "../../../types/PaginiationResult";
import { QueueSummary } from "../../../types/Queue/QueueSummary";
import { QueueItem, QueueItemSummary } from "../../../types/Queue/QueueItem";
import { QueueStatus } from "../../../types/Queue/QueueStatus";
import { ListQueueItemsParams } from "../../../types/Queue/ListQueueItemsParams";
import QueueItemDetails from "./QueueItemDetails";
import MarkAsResolved from "./MarkAsResolved";
import RetrySuccess from "./RetrySuccess";
import FailItem from "./FailItem";

const Queue = () => {
  const queueService = useQueueService();
  const [summary, setSummary] = useState<QueueSummary | undefined>();
  const [queueItems, setQueueItems] = useState<Pagination<QueueItemSummary>>({
    records: [],
    totalPages: 1,
    page: 1,
  });
  const [listQueueParams, setListQueueParams] = useState<ListQueueItemsParams>({
    queueStatus: QueueStatus.QUEUED,
    page: 1,
    pageSize: 10,
  });
  const [tableState, setTableState] = useState<ApiTableState>({
    pagination: {
      page: 1,
      pageSize: 10,
    },
  });
  const [loading, setLoading] = useState(false);
  const [detailItemId, setDetailItemId] = useState<string | undefined>();
  const [resolveItemId, setResolveItemId] = useState<string | undefined>();
  const [failItemId, setFailItemId] = useState<string | undefined>();
  const [showRetrySuccess, setShowRetrySuccess] = useState(false);
  const cols = [
    {
      field: "userFullName",
      label: "Ordering User",
      renderContents: (value: QueueItem) => <>{value.userFullName}</>,
      sortable: false,
    },
    {
      field: "organisationName",
      label: "Organisation",
      renderContents: (value: QueueItem) => <>{value.organisationName}</>,
      sortable: false,
    },
    {
      field: "layerName",
      label: "Product",
      renderContents: (value: QueueItem) => <>{value.layerName}</>,
      sortable: false,
    },
    {
      field: "areaDescription",
      label: "Area",
      renderContents: (value: QueueItem) => <>{value.areaDescription}</>,
      sortable: false,
    },
    {
      field: "queuedDate",
      label: "Queued",
      renderContents: (value: QueueItem) => (
        <>{value.queuedDate.toFormat("dd/MM/yyyy HH:mm")}</>
      ),
      sortable: false,
    },
    {
      field: "startedProcessingDate",
      label: "Started",
      renderContents: (value: QueueItem) => (
        <>
          {value.startedProcessingDate && (
            <>{value.startedProcessingDate.toFormat("dd/MM/yyyy HH:mm")}</>
          )}
        </>
      ),
      sortable: false,
    },
    {
      field: "completedDate",
      label: "Completed",
      renderContents: (value: QueueItem) => (
        <>
          {value.completedDate && (
            <>{value.completedDate?.toFormat("dd/MM/yyyy HH:mm")}</>
          )}
        </>
      ),
      sortable: false,
    },
    {
      field: "timeOnQueue",
      label: "Time on Queue",
      renderContents: (value: QueueItem) => (
        <>{value.timeOnQueue.toFixed(0)} mins</>
      ),
      sortable: false,
    },
    {
      field: "processingTime",
      label: "Processing Time",
      renderContents: (value: QueueItem) => (
        <>
          {value.processingTime && <>{value.processingTime.toFixed(0)} mins</>}
          {(value.queueStatus === QueueStatus.PROCESSING ||
            value.queueStatus === QueueStatus.FAILED) && (
            <> ({(value.progress * 100).toFixed(0)}%)</>
          )}
        </>
      ),
      sortable: false,
    },
    {
      field: "retryAttempt",
      label: "Retry Attempt",
      renderContents: (value: QueueItem) => (
        <>{value.retryAttempt.toFixed(0)}</>
      ),
      sortable: false,
    },
    {
      renderContents: (value: QueueItem) => (
        <Dropdown>
          <Dropdown.Toggle>
            <FontAwesomeIcon icon={faEllipsisV} />
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => setDetailItemId(value.deferredRequestId)}>
              View Details
            </Dropdown.Item>
            {value.queueStatus === QueueStatus.PROCESSING && (
              <Dropdown.Item
                onClick={() => setFailItemId(value.deferredRequestId)}>
                Fail
              </Dropdown.Item>
            )}
            {value.queueStatus === QueueStatus.FAILED && (
              <>
                <Dropdown.Item onClick={() => retry(value.orderItemId)}>
                  Retry
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setResolveItemId(value.orderItemId)}>
                  Mark As Resolved
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      ),
    },
  ] as ApiTableColumn[];

  const handleTabSelect = (eventKey: string | null) => {
    if (eventKey) {
      setListQueueParams({
        ...listQueueParams,
        page: 1,
        queueStatus: parseInt(eventKey) as QueueStatus,
      });
    }
  };

  const onStateChange = (tableState: ApiTableState) => {
    setTableState(tableState);
    setListQueueParams({
      ...listQueueParams,
      page: tableState.pagination.page,
      pageSize: tableState.pagination.pageSize,
    });
  };

  const retry = (orderItemId: string) => {
    setLoading(true);
    queueService
      .Retry(orderItemId)
      .then(() => {
        setListQueueParams({
          ...listQueueParams,
          page: 1,
          queueStatus: QueueStatus.QUEUED,
        });
        setShowRetrySuccess(true);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const markAsResolved = (orderItemId: string) => {
    queueService
      .ResolveFailedQueueItem(orderItemId)
      .then(() => {
        setListQueueParams({
          ...listQueueParams,
          page: 1,
        });
      })
      .catch(() => {});
  };

  const failItem = (deferredRequestId: string) => {
    queueService
      .Fail(deferredRequestId)
      .then(() => {
        setListQueueParams({
          ...listQueueParams,
          page: 1,
        });
      })
      .catch(() => {});
  };

  useEffect(() => {
    queueService
      .GetQueueSummary()
      .then((summary) => setSummary(summary))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    queueService
      .ListQueueItems(listQueueParams, abortController)
      .then((queueItems) => setQueueItems(queueItems))
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQueueParams]);

  return (
    <>
      {summary && (
        <>
          <h5>Queue Stats (over the last 7 days)</h5>
          <Card className="mb-2">
            <ListGroup horizontal={true}>
              <ListGroup.Item
                className="text-center"
                style={{ width: "33.33%" }}>
                Total Deferred Requests
                <div className="font-weight-bold" style={{ fontSize: "3rem" }}>
                  {summary.totalDeferredRequests}
                </div>
              </ListGroup.Item>
              <ListGroup.Item
                className="text-center"
                style={{ width: "33.33%" }}>
                Total Failed Requests
                <div className="font-weight-bold" style={{ fontSize: "3rem" }}>
                  {summary.totalFailedRequests}
                </div>
              </ListGroup.Item>
              <ListGroup.Item
                className="text-center"
                style={{ width: "33.33%" }}>
                Average Queue Time{" "}
                <div className="font-weight-bold" style={{ fontSize: "3rem" }}>
                  {summary.averageQueueTime.toFixed(0)}
                  <span style={{ fontSize: "1.2rem" }}> mins</span>
                </div>
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </>
      )}

      <Nav
        variant="tabs"
        activeKey={listQueueParams.queueStatus}
        onSelect={handleTabSelect}>
        <Nav.Item>
          <Nav.Link eventKey="1">Queue</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="2">Processing</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="3">Completed</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="-1">Failed</Nav.Link>
        </Nav.Item>
      </Nav>

      <ApiTable
        state={tableState}
        data={queueItems}
        columns={cols}
        pageOptions={[10, 20, 50]}
        onChange={onStateChange}
        allowRefresh={true}
        loading={loading}
      />

      {detailItemId && (
        <QueueItemDetails
          queueItemId={detailItemId}
          onClose={() => setDetailItemId(undefined)}
          retry={retry}
          markAsResolved={setResolveItemId}
        />
      )}
      {resolveItemId && (
        <MarkAsResolved
          orderItemId={resolveItemId}
          onResolve={() => markAsResolved(resolveItemId)}
          onClose={() => setResolveItemId(undefined)}
        />
      )}
      {showRetrySuccess && (
        <RetrySuccess onClose={() => setShowRetrySuccess(false)} />
      )}
      {failItemId && (
        <FailItem
          deferredRequestId={failItemId}
          onFail={() => failItem(failItemId)}
          onClose={() => setFailItemId(undefined)}
        />
      )}
    </>
  );
};

export default Queue;
